import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Mail,
  Lock,
  User as UserIcon,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { BrandMark } from '@/components/common/BrandMark'
import { useAuthStore } from '@/store/authStore'
import { CLINIC } from '@/lib/constants'

/* Inline Google logo — lucide doesn't ship a brand icon for Google. */
function GoogleIcon({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.24 1.46-1.7 4.3-5.5 4.3-3.31 0-6-2.74-6-6.1S8.69 6.2 12 6.2c1.88 0 3.14.8 3.86 1.48l2.64-2.54C16.78 3.65 14.6 2.7 12 2.7 6.97 2.7 2.9 6.78 2.9 11.8s4.07 9.1 9.1 9.1c5.26 0 8.74-3.7 8.74-8.9 0-.6-.06-1.06-.16-1.5H12z"
      />
      <path
        fill="#34A853"
        d="M3.66 7.34l3.21 2.36C7.78 7.5 9.7 6.2 12 6.2c1.88 0 3.14.8 3.86 1.48l2.64-2.54C16.78 3.65 14.6 2.7 12 2.7 8.32 2.7 5.12 4.78 3.66 7.34z"
      />
      <path
        fill="#FBBC05"
        d="M12 20.9c2.55 0 4.69-.84 6.25-2.3l-3.05-2.36c-.83.56-1.94.95-3.2.95-2.46 0-4.54-1.57-5.29-3.79l-3.18 2.45C5.05 18.66 8.27 20.9 12 20.9z"
      />
      <path
        fill="#4285F4"
        d="M20.74 12c0-.6-.06-1.06-.16-1.5H12v3.9h5.5c-.24 1.46-1.7 2.84-3.3 3.34l3.05 2.36C19.31 18.4 20.74 15.5 20.74 12z"
      />
    </svg>
  )
}

const MODES = {
  signin: 'signin',
  signup: 'signup',
  forgot: 'forgot',
}

export default function Login() {
  const navigate = useNavigate()
  const { signIn, signUp, signInWithGoogle, resetPassword, configured, unreachable } = useAuthStore()

  const [mode, setMode] = useState(MODES.signin)
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [submitting, setSubmitting] = useState(false)
  const [oauthLoading, setOauthLoading] = useState(false)

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value })

  const submit = async (e) => {
    e?.preventDefault?.()
    if (submitting) return

    if (!configured) {
      toast.error('Supabase is not configured. Check .env.')
      return
    }

    setSubmitting(true)
    try {
      if (mode === MODES.signin) {
        if (!form.email.trim() || !form.password) {
          toast.error('Email and password are required')
          return
        }
        await signIn({ email: form.email, password: form.password })
        toast.success('Welcome back')
        navigate('/', { replace: true })
      } else if (mode === MODES.signup) {
        if (!form.email.trim() || !form.password) {
          toast.error('Email and password are required')
          return
        }
        if (form.password.length < 6) {
          toast.error('Password must be at least 6 characters')
          return
        }
        const result = await signUp({
          email: form.email,
          password: form.password,
          name: form.name,
        })
        if (result?.session) {
          toast.success('Account created — signed in')
          navigate('/', { replace: true })
        } else {
          toast.success('Check your inbox to confirm your email')
          setMode(MODES.signin)
        }
      } else if (mode === MODES.forgot) {
        if (!form.email.trim()) {
          toast.error('Enter your email')
          return
        }
        await resetPassword(form.email)
        toast.success("If an account exists, we've sent reset instructions")
        setMode(MODES.signin)
      }
    } catch (err) {
      // Map Supabase's terse error strings to friendly messages
      const msg = err?.message || ''
      if (msg.includes('Invalid login credentials')) {
        toast.error('Wrong email or password. Try again or use Forgot password.')
      } else if (msg.includes('Email not confirmed')) {
        toast.error('Please confirm your email first — check your inbox.')
      } else if (msg.includes('User already registered')) {
        toast.error('An account with this email already exists. Try Sign in instead.')
      } else if (msg.includes('Password should be at least')) {
        toast.error('Password must be at least 6 characters.')
      } else if (msg.includes('Unable to validate') || msg.includes('not configured')) {
        toast.error('Supabase is not reachable. Check your .env settings.')
      } else {
        toast.error(msg || 'Authentication failed — please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleGoogle = async () => {
    if (!configured) {
      toast.error('Supabase is not configured. Check .env.')
      return
    }
    setOauthLoading(true)
    try {
      await signInWithGoogle()
      // The browser is being redirected to Google — keep the spinner on.
    } catch (err) {
      toast.error(err?.message || 'Google sign-in failed')
      setOauthLoading(false)
    }
  }

  const headline = {
    [MODES.signin]: 'Sign in to your clinic',
    [MODES.signup]: 'Create your account',
    [MODES.forgot]: 'Reset your password',
  }[mode]

  const eyebrow = {
    [MODES.signin]: 'Welcome back',
    [MODES.signup]: 'Get started',
    [MODES.forgot]: 'No worries',
  }[mode]

  const subtext = {
    [MODES.signin]: 'Sign in with your work email or continue with Google.',
    [MODES.signup]: 'New here? Create an account to start managing the clinic.',
    [MODES.forgot]: "We'll email you a link to set a new password.",
  }[mode]

  const ctaLabel = {
    [MODES.signin]: 'Sign in',
    [MODES.signup]: 'Create account',
    [MODES.forgot]: 'Send reset link',
  }[mode]

  return (
    <div className="min-h-screen bg-paper">
      <div className="mx-auto grid min-h-screen w-full max-w-7xl items-stretch gap-0 px-4 py-6 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-10 lg:py-12">
        {/* Left — pitch panel */}
        <aside className="hidden flex-col justify-between rounded-3xl bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 p-10 text-cream shadow-lift lg:flex">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream/15 ring-1 ring-cream/25">
              <BrandMark size={26} />
            </div>
            <div>
              {/* <p className="h-display text-lg font-bold">Radiance</p> */}
              <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-cream/70">
                Dental Clinic
              </p>
            </div>
          </div>

          <div>
            <h1 className="h-display text-balance text-4xl font-bold leading-[1.05]">
              Every patient's story,
              <br />
              on a single timeline.
            </h1>
            <p className="mt-4 max-w-md text-cream/80">
              Capture chief complaint, examination, prescription and consent — all in
              one place. Each visit preserves the full history, never overwrites.
            </p>

            <div className="mt-8 space-y-3">
              {[
                { icon: ShieldCheck, text: 'Personal info protected by a dedicated update flow' },
                { icon: Sparkles, text: 'Auto-generated patient IDs (RD000PT001 …)' },
              ].map(({ icon: Icon, text }, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-xl bg-cream/8 px-3 py-2.5 ring-1 ring-cream/15"
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-cream" />
                  <p className="text-sm text-cream/90">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-cream/60">
            <p>{CLINIC.address}</p>
            <p className="mt-1">{CLINIC.phone}</p>
          </div>
        </aside>

        {/* Right — form */}
        <div className="flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-700 text-cream shadow-soft">
                <BrandMark size={24} />
              </div>
              <div>
                {/* <p className="h-display text-base font-bold text-ink-900">Radiance</p> */}
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary-700">
                  Dental Clinic
                </p>
              </div>
            </div>

            <p className="label-text mb-2">{eyebrow}</p>
            <h2 className="h-display text-3xl font-bold text-ink-900">{headline}</h2>
            <p className="mt-2 text-sm text-ink-500">{subtext}</p>

            {!configured && (
              <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-rose-50 px-3.5 py-3 ring-1 ring-rose-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                <div className="text-xs text-rose-700">
                  <p className="font-semibold">Supabase not configured.</p>
                  <p className="mt-0.5">
                    Add <code className="rounded bg-rose-100 px-1">VITE_SUPABASE_URL</code> and{' '}
                    <code className="rounded bg-rose-100 px-1">VITE_SUPABASE_ANON_KEY</code> to your{' '}
                    <code className="rounded bg-rose-100 px-1">.env</code> file, then restart the dev server.
                  </p>
                </div>
              </div>
            )}

            {configured && unreachable && (
              <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-amber-50 px-3.5 py-3 ring-1 ring-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="text-xs text-amber-800">
                  <p className="font-semibold">Can't reach Supabase right now.</p>
                  <p className="mt-0.5">
                    Most likely the project is paused (Supabase auto-pauses free-tier projects after a
                    week of inactivity). Open the Supabase dashboard and click <strong>Restore project</strong>,
                    then refresh this page.
                  </p>
                </div>
              </div>
            )}

            {/* Google button — top of form, the recommended primary path */}
            {mode !== MODES.forgot && (
              <>
                <button
                  type="button"
                  onClick={handleGoogle}
                  disabled={oauthLoading || !configured}
                  className="mt-6 flex h-11 w-full items-center justify-center gap-3 rounded-xl bg-white px-4 text-sm font-semibold text-ink-900 ring-1 ring-inset ring-ink-200 transition hover:bg-ink-50 hover:ring-ink-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <GoogleIcon className="h-[18px] w-[18px]" />
                  {oauthLoading ? 'Redirecting to Google…' : 'Continue with Google'}
                </button>

                <div className="my-5 flex items-center gap-3">
                  <span className="h-px flex-1 bg-ink-100" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">
                    or continue with email
                  </span>
                  <span className="h-px flex-1 bg-ink-100" />
                </div>
              </>
            )}

            <form onSubmit={submit} className={mode === MODES.forgot ? 'mt-7 space-y-4' : 'space-y-4'}>
              {mode === MODES.signup && (
                <Input
                  label="Full name"
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Dr. Radha"
                  leftIcon={<UserIcon className="h-4 w-4" />}
                  autoComplete="name"
                />
              )}

              <Input
                label="Email"
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder="doctor@radiance.clinic"
                leftIcon={<Mail className="h-4 w-4" />}
                autoComplete="email"
                required
              />

              {mode !== MODES.forgot && (
                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={form.password}
                    onChange={update('password')}
                    placeholder="••••••••"
                    leftIcon={<Lock className="h-4 w-4" />}
                    autoComplete={mode === MODES.signup ? 'new-password' : 'current-password'}
                    required
                  />
                  {mode === MODES.signin && (
                    <button
                      type="button"
                      onClick={() => setMode(MODES.forgot)}
                      className="mt-1.5 ml-auto block text-xs font-semibold text-primary-700 hover:text-primary-800"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              )}

              <Button
                type="submit"
                loading={submitting}
                disabled={!configured || oauthLoading}
                rightIcon={!submitting ? <ArrowRight className="h-4 w-4" /> : null}
                className="w-full"
                size="lg"
              >
                {ctaLabel}
              </Button>
            </form>

            <div className="mt-6 space-y-1 text-center text-sm text-ink-500">
              {mode === MODES.signin && (
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode(MODES.signup)}
                    className="font-semibold text-primary-700 hover:text-primary-800"
                  >
                    Sign up
                  </button>
                </p>
              )}
              {mode === MODES.signup && (
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setMode(MODES.signin)}
                    className="font-semibold text-primary-700 hover:text-primary-800"
                  >
                    Sign in
                  </button>
                </p>
              )}
              {mode === MODES.forgot && (
                <p>
                  Remembered it?{' '}
                  <button
                    type="button"
                    onClick={() => setMode(MODES.signin)}
                    className="font-semibold text-primary-700 hover:text-primary-800"
                  >
                    Back to sign in
                  </button>
                </p>
              )}
            </div>

            <p className="mt-6 text-center text-xs text-ink-400">
               Call the clinic at  <span className="font-semibold text-ink-600">{CLINIC.phone}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
